import React, { useState } from 'react';
import classNames from 'classnames';
import { SectionProps } from '../../utils/SectionProps';
import Input from '../elements/Input';
import Button from '../elements/Button';

const propTypes = {
  ...SectionProps.types
}

const defaultProps = {
  ...SectionProps.defaults
}

const ContactForm = ({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor,
  ...props
}) => {

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    status: 'idle', // idle, loading, success, error
    message: ''
  });

  const outerClasses = classNames(
    'contact-form section',
    topOuterDivider && 'has-top-divider',
    bottomOuterDivider && 'has-bottom-divider',
    hasBgColor && 'has-bg-color',
    invertColor && 'invert-color',
    className
  );

  const innerClasses = classNames(
    'contact-form-inner section-inner',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider'
  );

  const tilesClasses = classNames(
    'tiles-wrap center-content'
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ status: 'loading', message: '' });

    // Validate
    if (!formData.firstName || !formData.email || !formData.message) {
      setFormStatus({
        status: 'error',
        message: 'Please fill in required fields (Name, Email, Message).'
      });
      return;
    }

    try {
      const response = await fetch('/api/hubspot-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to submit form');
      }
      
      setFormStatus({
        status: 'success',
        message: 'Your inquiry has been sent successfully. We will be in touch shortly!'
      });
      
      // Clear form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        message: ''
      });

    } catch (error) {
      console.error(error);
      setFormStatus({
        status: 'error',
        message: 'An error occurred while sending your message. Please try again.'
      });
    }
  };

  return (
    <section
      {...props}
      className={outerClasses}
    >
      <div className="container">
        <div className={innerClasses} style={{ maxWidth: '100%', margin: '0 auto' }}>
          <div className="section-header center-content">
            <h2 className="mt-0 mb-16">Get in Touch</h2>
            <p className="m-0 text-sm">
              Have questions about our products or need support? Fill out the form below and our team will get back to you.
            </p>
          </div>
          
          <div className={tilesClasses}>
            <div className="tiles-item reveal-from-bottom" data-reveal-container=".tiles-wrap" style={{ width: '100%', maxWidth: '100%', margin: '0 auto' }}>
              <div className="tiles-item-inner has-shadow" style={{ textAlign: 'left', padding: '36px', backgroundColor: '#141C2E', border: '1px solid #1E293B', borderRadius: '8px' }}>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                   <div style={{ flex: 1 }}>
                     <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        label="First Name *"
                        labelHidden
                        placeholder="First Name *"
                        value={formData.firstName}
                        onChange={handleInputChange}
                      />
                   </div>
                   <div style={{ flex: 1 }}>
                     <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        label="Last Name"
                        labelHidden
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                      />
                   </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                     <Input
                        id="email"
                        name="email"
                        type="email"
                        label="Email Address *"
                        labelHidden
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                  </div>

                  <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontFamily: '"Inter", sans-serif', color: '#A88756', marginBottom: '6px', fontWeight: '600' }}>Holding Division *</label>
                      <select 
                        id="division" 
                        name="division" 
                        value={formData.division || 'trade'} 
                        onChange={handleInputChange}
                        style={{ width: '100%', height: '44px', fontFamily: '"Inter", sans-serif', fontSize: '14px', backgroundColor: '#0A0F1D', color: '#CBD5E1', border: '1px solid #1E293B', borderRadius: '4px', padding: '0 12px' }}
                      >
                        <option value="trade">Global Trade & Sourcing Group</option>
                        <option value="tech">Technology & Digital Solutions Group</option>
                        <option value="general">General Executive Inquiry</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontFamily: '"Inter", sans-serif', color: '#A88756', marginBottom: '6px', fontWeight: '600' }}>Target Region *</label>
                      <select 
                        id="region" 
                        name="region" 
                        value={formData.region || 'americas'} 
                        onChange={handleInputChange}
                        style={{ width: '100%', height: '44px', fontFamily: '"Inter", sans-serif', fontSize: '14px', backgroundColor: '#0A0F1D', color: '#CBD5E1', border: '1px solid #1E293B', borderRadius: '4px', padding: '0 12px' }}
                      >
                        <option value="americas">Americas (US Headquarters)</option>
                        <option value="mea">MEA (Middle East & Africa / UAE Hubs)</option>
                        <option value="asia">Asia</option>
                        <option value="global">Global / Cross-Border</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                     <Input
                        id="company"
                        name="company"
                        type="text"
                        label="Company / Business Name"
                        labelHidden
                        placeholder="Company / Business Name (Optional)"
                        value={formData.company}
                        onChange={handleInputChange}
                      />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                     <Input
                        id="address"
                        name="address"
                        type="text"
                        label="Address"
                        labelHidden
                        placeholder="Address (Optional)"
                        value={formData.address}
                        onChange={handleInputChange}
                      />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                     <Input
                        id="message"
                        name="message"
                        type="textarea"
                        label="Message *"
                        labelHidden
                        placeholder="How can we help you? *"
                        value={formData.message}
                        onChange={handleInputChange}
                        style={{ height: '120px' }}
                      />
                  </div>

                  {formStatus.status === 'error' && (
                    <div style={{ color: '#EF4444', marginBottom: '16px', fontSize: '14px' }}>
                      {formStatus.message}
                    </div>
                  )}

                  {formStatus.status === 'success' && (
                    <div style={{ color: '#10B981', marginBottom: '16px', fontSize: '14px' }}>
                      {formStatus.message}
                    </div>
                  )}

                  <Button color="primary" wideMobile type="submit" disabled={formStatus.status === 'loading'}>
                    {formStatus.status === 'loading' ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

ContactForm.propTypes = propTypes;
ContactForm.defaultProps = defaultProps;

export default ContactForm;
